import Forms from '../../../../../../../../../Template/layoutComponents/Forms';
import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'antd';

import './style.css';
import useTemplateState from '../../../../../../../../../Template/atoms/variables.js';
import { createIssue, updateIssue } from '../../../../../../../../helpers/apiCalls.js';
import { useMsal } from '@azure/msal-react';
import { getUsers } from '../../../../../../../../../Settings/helpers/apiCalls.js';
import { useParams } from 'react-router-dom';

const IssueDetailsModal = ({ stage, givenIssue, handleInsertComment, setForceReload }) => {
  const { setPendingApiCalls, setModalOpen, setModalContent, setModalStyle } = useTemplateState();
  const { t } = useTranslation();

  const { assetId, interconnectionId } = useParams();

  const { instance } = useMsal();
  const [issue, setIssue] = useState(givenIssue);
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState(null);
  const [selectedStage, setSelectedStage] = useState(stage || (givenIssue?.stage));
  
  // Track form values separately
  const [formValues, setFormValues] = useState({
    title: givenIssue?.title || '',
    status: givenIssue?.status || '',
    priority: givenIssue?.priority || '',
    executor: givenIssue?.executor?._id || givenIssue?.assigned_to?._id || '',
    due_date: givenIssue?.due_date ? new Date(givenIssue.due_date).toISOString().split('T')[0] : '',
    description: givenIssue?.description || '',
  });

  // Check if all required fields are filled
  const isFormValid = useMemo(() => {
    return formValues.title && 
           (selectedStage || stage) && 
           formValues.status && 
           formValues.priority && 
           formValues.executor && 
           formValues.due_date;
  }, [formValues, selectedStage, stage]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const baseProps = [
      {
        type: 'text',
        value: formValues.title,
        required: true,
        disabled: !!givenIssue,
        key: 'title',
      },
      // Add stage selector if no stage is provided
      ...(!stage && !givenIssue ? [{
        type: 'singleSelect',
        value: selectedStage ? { id: selectedStage } : null,
        options: [
          { id: 'L1', label: 'L1' },
          { id: 'L2', label: 'L2' },
          { id: 'L3', label: 'L3' },
          { id: 'L4', label: 'L4' },
          { id: 'L5', label: 'L5' },
        ],
        required: true,
        key: 'stage',
      }] : []),
      // Show stage as text if editing existing issue or stage is provided
      ...(stage || givenIssue ? [{
        type: 'text',
        value: stage || givenIssue?.stage,
        required: true,
        disabled: true,
        key: 'stage_display',
        label: 'Stage',
      }] : []),
      {
        type: 'singleSelect',
        value: formValues.status ? { id: formValues.status } : null,
        options: [
          { id: 'Pending', label: 'Pending' },
          { id: 'Ongoing', label: 'Ongoing' },
          { id: 'Closed', label: 'Closed' },
        ].filter((option) => {
          if (!givenIssue) {
            return option.id !== 'Closed';
          }
          return true;
        }),
        required: true,
        key: 'status',
      },
      {
        type: 'singleSelect',
        value: formValues.priority ? { id: formValues.priority } : null,
        options: [
          { id: 'Low', label: 'Low' },
          { id: 'Medium', label: 'Medium' },
          { id: 'High', label: 'High' },
        ],
        required: true,
        key: 'priority',
      },
      {
        type: 'singleSelect',
        value: formValues.executor ? { id: formValues.executor } : null,
        options: users?.map((user) => ({ ...user, id: user._id, label: user.email })),
        required: true,
        key: 'executor',
      },
      {
        type: 'date',
        value: formValues.due_date,
        required: true,
        key: 'due_date',
      },
      {
        type: 'textarea',
        value: formValues.description,
        required: true,
        key: 'description',
      },
    ];

    setProps(baseProps);
  }, [formValues, users, selectedStage, stage, givenIssue]);

  const fetchUsers = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['getUsers']));
      const usersResponse = await getUsers(instance);
      setUsers(usersResponse.items);
    } catch (err) {
      console.log(err);
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'getUsers'));
    }
  };

  const handleUpdateIssue = async () => {
    try {
      setPendingApiCalls((current) => current.concat(['updateIssue']));

      const updatedIssue = {
        title: formValues.title,
        status: formValues.status,
        priority: formValues.priority,
        executor: formValues.executor,
        due_date: formValues.due_date,
        description: formValues.description,
      };

      if (givenIssue) {
        await updateIssue(instance, { ...givenIssue, ...updatedIssue });
      } else {
        await createIssue(instance, {
          ...updatedIssue,
          subjectId: assetId ?? interconnectionId,
          kind: assetId ? 'asset' : 'interconnection',
          stage: stage || selectedStage,
        });
      }

      if (setForceReload) setForceReload(true);

      setModalOpen(false);
      setModalContent(null);
      setModalStyle(null);
    } catch (err) {
      console.log(err);
    } finally {
      setPendingApiCalls((current) => current.filter((item) => item !== 'updateIssue'));
    }
  };

  const handleInputChange = (event, inputIndex) => {
    setProps((current) => {
      const updatedProps = [...current];
      const currentValue = updatedProps[inputIndex];

      // For singleSelect and radio inputs, wrap the value in an object with id
      const newValue =
        currentValue.type === 'singleSelect' || currentValue.type === 'radio'
          ? { id: event.target.value }
          : event.target.value;

      updatedProps[inputIndex] = { ...currentValue, value: newValue };

      // Update formValues
      setFormValues(prev => ({
        ...prev,
        [currentValue.key]: currentValue.type === 'singleSelect' ? newValue.id : newValue
      }));

      // Update selectedStage if stage field is changed
      if (currentValue.key === 'stage') {
        setSelectedStage(newValue.id);
      }

      // If the changed prop is priority and due_date is not set, set due_date based on priority
      if (
        currentValue.key === 'priority' &&
        !updatedProps.find((p) => p.key === 'due_date')?.value
      ) {
        const dueDateIndex = updatedProps.findIndex((p) => p.key === 'due_date');
        if (dueDateIndex !== -1) {
          const today = new Date();
          const daysToAdd = {
            Low: 20,
            Medium: 10,
            High: 5,
          }[newValue.id];

          if (daysToAdd !== undefined) {
            today.setDate(today.getDate() + daysToAdd);
            const dueDateValue = today.toISOString().split('T')[0];
            updatedProps[dueDateIndex] = {
              ...updatedProps[dueDateIndex],
              value: dueDateValue,
            };
          }
        }
      }

      return updatedProps;
    });
  };

  return (
    <div className='issue-details-modal'>
      <Forms
        id={issue?._id}
        inputs={props}
        onInputBlur={() => {}}
        onInputChange={handleInputChange}
      />
      <div className='issue-details-modal-buttons'>
        <Button
          className='cancel-btn'
          size='large'
          style={{
            color: '#242424',
            border: '1px solid #242424',
            backgroundColor: 'white',
          }}
          onClick={() => {
            setModalOpen(false);
            setModalContent(null);
            setModalStyle(null);
          }}
        >
          {t('cancel-btn')}
        </Button>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            className='comment-btn'
            size='large'
            disabled={!givenIssue}
            style={{
              color: '#1777ff',
              border: '1px solid #1777ff',
              backgroundColor: 'white',
              opacity: givenIssue ? 1 : 0.5,
            }}
            onClick={handleInsertComment}
          >
            {t('add_comment')}
          </Button>
          <Button
            size='large'
            style={{
              color: 'white',
              border: '#1777ff',
              backgroundColor: '#1777ff',
              opacity: isFormValid ? 1 : 0.5,
            }}
            disabled={!isFormValid}
            onClick={handleUpdateIssue}
          >
            {t('save')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default IssueDetailsModal;
